import { useState, useEffect } from 'react';
import { USER_ACCESS_LEVEL, PRODUCT_KEY } from '../../utils/constants';
import { isProductEnabled } from '../../utils/productAccess';
import {
    getActiveChatInsiderProgramService,
} from '../../services/learning_programs.service';
import { getOrgModulesService } from '../../services/client.service';

/**
 * useSituationalTutorials
 *
 * Centralises the data-fetching and trigger logic for contextual, one-time
 * tutorials shown on /my_workspace for Level-21 users.
 *
 * Returns:
 *   shouldShowClassesNudge  — Tutorial A: exactly 1 completed program
 *   shouldShowChatBanner    — Tutorial B: active chat + unbuilt program
 *
 * Both flags are guarded by per-user localStorage keys so they fire at most once.
 *
 * @param {object|null} authUser — The authenticated user from AuthContext
 */
const useSituationalTutorials = (authUser) => {
    const [shouldShowClassesNudge, setShouldShowClassesNudge] = useState(false);
    const [shouldShowChatBanner,   setShouldShowChatBanner]   = useState(false);

    useEffect(() => {
        // ── Guard: Level 21 only, auth must be clean ─────────────────────────
        if (!authUser) return;
        if (authUser.access_level !== USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE) return;
        if (!authUser.is_email_verified)       return;
        if (authUser.required_password_reset)  return;

        const userId          = authUser.id;
        const classesNudgeSeen = localStorage.getItem(`tutorial_classes_nudge_${userId}`);
        const chatNudgeSeen    = localStorage.getItem(`tutorial_chat_nudge_${userId}`);

        // Both nudges already dismissed — skip all API calls
        if (classesNudgeSeen && chatNudgeSeen) return;

        let isMounted = true;

        Promise.allSettled([
            getOrgModulesService({ org_code: authUser?.org_code, module_type: 'learning_program', page: 1, limit: 10 }),
            getActiveChatInsiderProgramService(),
        ]).then(([modulesResult, chatResult]) => {
            if (!isMounted) return;

            // Parse org modules (completed programs)
            const modulesPayload = modulesResult.status === 'fulfilled'
                ? (modulesResult.value?.data ?? modulesResult.value ?? {})
                : {};
            const modules = (modulesPayload?.modules || []);

            // Parse active chat
            const chatPayload = chatResult.status === 'fulfilled'
                ? (chatResult.value?.data ?? chatResult.value ?? {})
                : {};
            const activeChatProgramId = chatPayload?.program_id;

            // ── Tutorial A: "Program Complete → Link to Classes"
            //    Condition: exactly 1 completed program (org module) exists
            if (!classesNudgeSeen && modules.length === 1) {
                setShouldShowClassesNudge(true);
            }

            // ── Tutorial B: "Active Chat, Build Not Started"
            //    Condition: active chat exists AND no completed programs (org modules) exist AND user has learning_program access
            if (!chatNudgeSeen && activeChatProgramId && modules.length === 0 && isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM)) {
                setShouldShowChatBanner(true);
            }
        }).catch(() => {
            // Silently swallow — tutorial nudges are non-critical
        });

        return () => { isMounted = false; };
    }, [authUser]);

    return { shouldShowClassesNudge, shouldShowChatBanner };
};

export default useSituationalTutorials;
